import React, { useEffect, useMemo, useRef, useState } from 'react';
import { debeParpadear, estiloEstado, etiquetaEstado, formatearFecha, hoyISO, tieneResultado, tocaComunicar } from './estadosPartido';
import { COLUMNAS, ORDEN_INICIAL, ordenarPartidos, siguienteOrden } from './ordenPartidos';

function DetalleResultado({ partido }) {
  const sets = partido.sets ?? [];
  return (
    <div className="mz-detalle">
      {sets.length > 0 ? (
        <ul className="mz-sets">
          {sets.map((s) => (
            <li key={s.numero}>
              Set {s.numero}: <strong>{s.puntosLocal}–{s.puntosVisitante}</strong>
              {s.adjudicado && <span className="mz-adjudicado"> (adjudicado{s.motivoAdjudicacion ? `: ${s.motivoAdjudicacion}` : ''})</span>}
            </li>
          ))}
        </ul>
      ) : (
        <p className="mz-sin-sets">Sin puntuaciones por set (resultado registrado por el comité).</p>
      )}
      <p className="mz-marcador">
        Resultado: <strong>{partido.sets_local ?? '—'}–{partido.sets_visitante ?? '—'}</strong> en sets
        {partido.clasif_local != null && (
          <> · Puntos de clasificación: <strong>{partido.clasif_local}–{partido.clasif_visitante}</strong></>
        )}
      </p>
    </div>
  );
}

// Revisión del comité del justificante de aplazamiento (null = sin justificante).
const textoJustificante = (partido) => {
  switch (partido.justificante_estado) {
    case 'PENDIENTE': return 'Justificante pendiente de revisión del comité';
    case 'APROBADO': return `Justificante aprobado · ${partido.fecha ? `nueva fecha ${formatearFecha(partido.fecha)}` : 'nueva fecha por fijar'}`;
    case 'RECHAZADO': return 'Justificante rechazado';
    default: return null;
  }
};

// Fecha de la tarjeta móvil: la jugada si existe; aplazado sin jugar = pendiente;
// si no, la de la jornada.
// Pista y hora del cuadrante de /competicion (solo si está publicado).
const textoPista = (p) => (p.pista && p.hora_inicio ? `${p.hora_inicio} h · Pista ${p.pista}` : null);

const fechaTarjeta = (p) => {
  if (p.id === null) return formatearFecha(p.fecha);
  if (p.fecha_jugado) return formatearFecha(p.fecha_jugado);
  return p.estado === 'APLAZADO' ? 'Pendiente' : formatearFecha(p.fecha);
};

// Icono de «te toca comunicar el resultado por WhatsApp» (eres local). Con ratón
// el tooltip sale al pasar/enfocar (CSS, hover:hover); en pantallas táctiles se
// abre al pulsar y se cierra al pulsar fuera.
function AvisoWhatsapp({ modalidad, parpadea = false, clase = '' }) {
  const [abierto, setAbierto] = useState(false);
  const ref = useRef(null);
  const texto = `Te toca comunicar resultados en el grupo "MODALIDAD ${modalidad}" de la comunidad whatsapp`;

  useEffect(() => {
    if (!abierto) return undefined;
    const cerrarFuera = (e) => { if (!ref.current?.contains(e.target)) setAbierto(false); };
    document.addEventListener('pointerdown', cerrarFuera);
    return () => document.removeEventListener('pointerdown', cerrarFuera);
  }, [abierto]);

  return (
    <span ref={ref} className={['mz-wa', parpadea && 'mz-wa-parpadeo', clase].filter(Boolean).join(' ')}>
      <button type="button" className="mz-wa-btn" aria-label={texto} aria-expanded={abierto} onClick={() => setAbierto((v) => !v)}>
        <img src={`${process.env.PUBLIC_URL}/comunicar_whatsapp.png`} alt="" width="22" height="22" />
      </button>
      <span role="tooltip" className={abierto ? 'mz-wa-tip mz-wa-abierto' : 'mz-wa-tip'}>{texto}</span>
    </span>
  );
}

function Fila({ partido, estados, onRevision }) {
  const [abierta, setAbierta] = useState(false);
  const estado = estados[partido.estado_codigo];
  const plegable = tieneResultado(partido);
  const esDescanso = partido.id === null;
  const tocaWhatsapp = tocaComunicar(partido);
  const parpadea = debeParpadear(partido, hoyISO());
  const idDetalle = `mz-detalle-${partido.id}`;

  return (
    <>
      <tr className={estado?.banner ? 'mz-fila mz-fila-banner' : 'mz-fila'}>
        <td data-label="Jornada" className="mz-celda-jornada">
          {plegable && (
            <button
              type="button" className="mz-plegar" aria-expanded={abierta} aria-controls={idDetalle}
              aria-label={`${abierta ? 'Ocultar' : 'Ver'} puntuaciones de la jornada ${partido.jornada}`}
              onClick={() => setAbierta((v) => !v)}
            >
              {abierta ? '▾' : '▸'}
            </button>
          )}
          <span className="mz-badge-jornada">{partido.jornada}</span>
        </td>
        <td data-label="Fecha" className="mz-solo-escritorio">
          {formatearFecha(partido.fecha)}
          {textoPista(partido) && <small className="mz-pista">{textoPista(partido)}</small>}
        </td>
        <td data-label="Local" className={partido.lado === 'LOCAL' ? 'mz-mio mz-solo-escritorio' : 'mz-solo-escritorio'}>{partido.local_nombre ?? '—'}</td>
        <td data-label="Visitante" className={partido.lado === 'VISITANTE' ? 'mz-mio mz-solo-escritorio' : 'mz-solo-escritorio'}>{partido.visitante_nombre ?? '—'}</td>
        <td data-label="Fase" className="mz-solo-escritorio">{partido.tipo === 'VUELTA' ? 'Vuelta' : 'Ida'}</td>
        {/* Solo móvil (tarjeta): fecha única y enfrentamiento «yo vs contrincante» con la fase. */}
        <td className="mz-solo-movil mz-fecha-movil">
          {fechaTarjeta(partido)}
          {textoPista(partido) && <small className="mz-pista">{textoPista(partido)}</small>}
        </td>
        <td className="mz-solo-movil mz-partido-movil">
          {esDescanso ? null : (
            <>
            <span className={partido.lado === 'LOCAL' ? 'mz-mio' : undefined}>{partido.local_nombre ?? '—'}</span>
            <span className="mz-vs">vs</span>
            <span className={partido.lado === 'LOCAL' ? undefined : 'mz-mio'}>{partido.visitante_nombre ?? '—'}</span>
            </>
          )}
          <span className="mz-badge-fase">{partido.tipo === 'VUELTA' ? 'VUELTA' : 'IDA'}</span>
          {tocaWhatsapp && <AvisoWhatsapp modalidad={partido.modalidad} parpadea={parpadea} />}
        </td>
        <td data-label="Estado" className="mz-celda-estado">
          <span className={estado?.banner ? 'mz-estado mz-estado-banner' : 'mz-estado'} style={estiloEstado(estado)}>
            {etiquetaEstado(estados, partido.estado_codigo)}
          </span>
          {tocaWhatsapp && <AvisoWhatsapp modalidad={partido.modalidad} parpadea={parpadea} clase="mz-solo-escritorio" />}
          {textoJustificante(partido) && <small className="mz-justificante">{textoJustificante(partido)}</small>}
        </td>
        <td data-label="Fecha jugado" className="mz-solo-escritorio">{esDescanso ? '—' : formatearFecha(partido.fecha_jugado)}</td>
        <td data-label="Revisión" className="mz-celda-revision">
          {!esDescanso && (
            <button
              type="button" className="btn btn-secondary btn-sm"
              aria-label={`Solicitar revisión de la jornada ${partido.jornada}`}
              onClick={() => onRevision(partido)}
            >
              Solicitar revisión
            </button>
          )}
        </td>
      </tr>
      {plegable && abierta && (
        <tr className="mz-fila-detalle" id={idDetalle}>
          <td colSpan={COLUMNAS.length + 1}><DetalleResultado partido={partido} /></td>
        </tr>
      )}
    </>
  );
}

// Una tabla por modalidad en la que está inscrito el jugador. Se ordena
// pulsando la cabecera de cada columna (cada tabla lleva su propio orden).
// `hayFiltro` solo cambia el mensaje cuando no queda ninguna fila.
export default function TablaPartidos({ modalidad, partidos, estados, hayFiltro, onRevision }) {
  const [orden, setOrden] = useState(ORDEN_INICIAL);
  const filas = useMemo(() => ordenarPartidos(partidos, orden, estados), [partidos, orden, estados]);
  const flecha = (clave) => (orden.columna === clave ? (orden.direccion === 'asc' ? ' ▲' : ' ▼') : '');

  return (
    <section className="mz-modalidad" aria-label={modalidad}>
      <h3>{modalidad}</h3>
      {partidos.length === 0 ? (
        <p className="mz-vacio">{hayFiltro ? 'Ningún partido con ese estado en esta modalidad.' : 'Todavía no hay partidos en esta modalidad.'}</p>
      ) : (
        <>
        {/* En móvil la cabecera de la tabla se oculta (tarjetas): el orden se elige aquí. */}
        <div className="mz-orden-movil">
          <label htmlFor={`mz-orden-${modalidad}`}>Ordenar por</label>
          <select
            id={`mz-orden-${modalidad}`} value={orden.columna}
            onChange={(e) => setOrden({ columna: e.target.value, direccion: 'asc' })}
          >
            {COLUMNAS.map((c) => <option key={c.clave} value={c.clave}>{c.titulo}</option>)}
          </select>
          <button
            type="button" className="btn btn-secondary btn-sm"
            onClick={() => setOrden((o) => ({ ...o, direccion: o.direccion === 'asc' ? 'desc' : 'asc' }))}
            aria-label={`Sentido del orden: ${orden.direccion === 'asc' ? 'ascendente' : 'descendente'}`}
          >
            {orden.direccion === 'asc' ? '▲' : '▼'}
          </button>
        </div>
        <table className="data-table mz-tabla">
          <thead>
            <tr>
              {COLUMNAS.map((c) => (
                <th
                  key={c.clave}
                  aria-sort={orden.columna === c.clave ? (orden.direccion === 'asc' ? 'ascending' : 'descending') : 'none'}
                >
                  <button type="button" className="mz-th-orden" onClick={() => setOrden((o) => siguienteOrden(o, c.clave))}>
                    {c.titulo}{flecha(c.clave)}
                  </button>
                </th>
              ))}
              <th><span className="mz-th-orden mz-th-sin-orden">Revisión</span></th>
            </tr>
          </thead>
          <tbody>
            {filas.map((p) => (
              <Fila key={p.id ?? `d-${p.jornada}-${p.tipo}`} partido={p} estados={estados} onRevision={onRevision} />
            ))}
          </tbody>
        </table>
        </>
      )}
    </section>
  );
}
